import { FormField } from '@/components/form-section'
import SenderDepartmentSelect from '@/components/sales/SenderDepartmentSelect'
import type { SalesFlowSettingsData } from '@/lib/api/sales/flows-api'
import type {
    SalesFlowActiveWindow,
    SalesFlowExitRules,
} from '@mediardex/contracts'
import { FLOW_EXIT_RULE_LABELS } from '../flow-meta'
import DurationInput from './duration-input'

const WEEKDAYS = [
    { value: 1, label: 'Seg' },
    { value: 2, label: 'Ter' },
    { value: 3, label: 'Qua' },
    { value: 4, label: 'Qui' },
    { value: 5, label: 'Sex' },
    { value: 6, label: 'Sáb' },
    { value: 7, label: 'Dom' },
]

const DEFAULT_WINDOW: SalesFlowActiveWindow = {
    timezone: 'America/Sao_Paulo',
    days: [1, 2, 3, 4, 5],
    from: '08:00',
    to: '18:00',
}

/** Nome, setor, horário de envio, regras de saída e reentrada. */
export default function SettingsPanel({
    settings,
    onChange,
}: {
    settings: Required<Pick<SalesFlowSettingsData, 'name'>> &
        SalesFlowSettingsData
    onChange: (patch: SalesFlowSettingsData) => void
}) {
    const activeWindow = settings.activeWindow ?? null
    const exitRules = settings.exitRules ?? {}

    return (
        <div className="space-y-6">
            <FormField label="Nome" htmlFor="flow-name" required>
                <input
                    id="flow-name"
                    className="input input-bordered input-sm w-full"
                    value={settings.name}
                    onChange={e => onChange({ name: e.target.value })}
                />
            </FormField>

            <FormField label="Descrição" htmlFor="flow-description" optional>
                <textarea
                    id="flow-description"
                    className="textarea textarea-bordered w-full text-sm leading-relaxed"
                    rows={3}
                    value={settings.description ?? ''}
                    onChange={e => onChange({ description: e.target.value })}
                />
            </FormField>

            <FormField
                label="Setor padrão de envio"
                htmlFor="flow-department"
                hint="Vale para os passos de mensagem que não escolhem setor. Vazio: setor da conversa do lead."
            >
                <SenderDepartmentSelect
                    value={settings.departmentId}
                    onChange={departmentId => onChange({ departmentId })}
                    emptyHint={null}
                />
            </FormField>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-base-300 p-3">
                <input
                    type="checkbox"
                    className="toggle toggle-primary toggle-sm mt-0.5"
                    checked={!!settings.launchable}
                    onChange={e => onChange({ launchable: e.target.checked })}
                />
                <span>
                    <span className="block text-sm font-medium">
                        Disponível na barra do chat
                    </span>
                    <span className="mt-0.5 block text-xs text-base-content/50">
                        O atendente inicia o fluxo para o contato da conversa.
                    </span>
                </span>
            </label>

            <div className="space-y-3">
                <label className="flex cursor-pointer items-start gap-3">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm mt-0.5"
                        checked={!!activeWindow}
                        onChange={e =>
                            onChange({
                                activeWindow: e.target.checked
                                    ? DEFAULT_WINDOW
                                    : null,
                            })
                        }
                    />
                    <span>
                        <span className="block text-sm font-medium">
                            Enviar só em horário definido
                        </span>
                        <span className="mt-0.5 block text-xs text-base-content/50">
                            Fora dele, a mensagem espera a próxima abertura.
                            Ações de CRM não esperam.
                        </span>
                    </span>
                </label>

                {activeWindow && (
                    <div className="space-y-3 rounded-xl border border-base-300 bg-base-200/40 p-3">
                        <div className="flex flex-wrap gap-1.5">
                            {WEEKDAYS.map(day => {
                                const on = activeWindow.days.includes(day.value)
                                return (
                                    <button
                                        key={day.value}
                                        type="button"
                                        className={`btn btn-xs ${on ? 'btn-primary' : 'btn-ghost border-base-300'}`}
                                        onClick={() =>
                                            onChange({
                                                activeWindow: {
                                                    ...activeWindow,
                                                    days: on
                                                        ? activeWindow.days.filter(
                                                              d =>
                                                                  d !==
                                                                  day.value
                                                          )
                                                        : [
                                                              ...activeWindow.days,
                                                              day.value,
                                                          ].sort(),
                                                },
                                            })
                                        }
                                    >
                                        {day.label}
                                    </button>
                                )
                            })}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                label="Das"
                                htmlFor="flow-activeWindow-from"
                            >
                                <input
                                    id="flow-activeWindow-from"
                                    type="time"
                                    className="input input-bordered input-sm w-full"
                                    value={activeWindow.from}
                                    onChange={e =>
                                        onChange({
                                            activeWindow: {
                                                ...activeWindow,
                                                from: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </FormField>
                            <FormField
                                label="Até"
                                htmlFor="flow-activeWindow-to"
                            >
                                <input
                                    id="flow-activeWindow-to"
                                    type="time"
                                    className="input input-bordered input-sm w-full"
                                    value={activeWindow.to}
                                    onChange={e =>
                                        onChange({
                                            activeWindow: {
                                                ...activeWindow,
                                                to: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </FormField>
                        </div>
                    </div>
                )}
            </div>

            <FormField
                label="Parar o fluxo quando"
                hint="Vale para quem já está no fluxo."
            >
                <div className="space-y-2">
                    {(
                        Object.keys(
                            FLOW_EXIT_RULE_LABELS
                        ) as (keyof SalesFlowExitRules)[]
                    ).map(rule => (
                        <label
                            key={rule}
                            className="flex cursor-pointer items-start gap-3 rounded-lg px-1 py-1"
                        >
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm mt-0.5"
                                checked={!!exitRules[rule]}
                                onChange={e =>
                                    onChange({
                                        exitRules: {
                                            ...exitRules,
                                            [rule]: e.target.checked,
                                        },
                                    })
                                }
                            />
                            <span>
                                <span className="block text-sm">
                                    {FLOW_EXIT_RULE_LABELS[rule].label}
                                </span>
                                {FLOW_EXIT_RULE_LABELS[rule].hint && (
                                    <span className="block text-xs text-base-content/50">
                                        {FLOW_EXIT_RULE_LABELS[rule].hint}
                                    </span>
                                )}
                            </span>
                        </label>
                    ))}
                </div>
            </FormField>

            <FormField
                label="Intervalo para reiniciar no mesmo lead"
                htmlFor="flow-cooldown"
                optional
                hint="Depois que o lead termina o fluxo, ele só entra de novo passado esse tempo. Vazio: pode entrar de novo logo."
            >
                <DurationInput
                    id="flow-cooldown"
                    value={(settings.reentryCooldownHours ?? 0) * 60}
                    onChange={minutes =>
                        onChange({
                            reentryCooldownHours: minutes
                                ? Math.max(1, Math.round(minutes / 60))
                                : null,
                        })
                    }
                />
            </FormField>
        </div>
    )
}
